// SCR-STAFF-601 — فرم‌ساز (US-DOC-020 تا 024): فهرست قالب‌ها با نسخه و وضعیت، کتابخانه نمونه بسته‌های تخصصی (کپی ویرایش‌پذیر با نشان «نمونه»)،
// ویرایشگر بخش و فیلد (۱۴ نوع، کد یکتا، برچسب فارسی/انگلیسی، الزامی، اعتبارسنجی، حساسیت، شرط نمایش روی پاسخ یا جنسیت/سن، نگاشت CLN/PAT) با
// پیش‌نمایش زنده، انتشار نسخه (فرم‌های قبلی با نسخه خود)، بایگانی و بازگردانی.
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type DocFormTemplateView, type DocFormType } from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { type Answers, asDefinition, DataTable, type DataTableColumn, ErrorState, faDigits, FIELD_TYPES, type FormDefinitionJson, FormDialog, type FormFieldDef, FormRenderer, type FormSectionDef, ListPage, SampleBadge, SampleContentBanner, type ScoringBandDef, type ScoringDef, type ScoringScaleDef } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { WriteNotice } from '../settings/common';
import { docKeys, num, SCR_STAFF_601 } from './common';

const FORM_TYPES: DocFormType[] = ['Administrative', 'Clinical', 'Questionnaire', 'PreVisit'];
const OPS = ['eq', 'ne', 'truthy', 'in', 'gt', 'gte', 'lt', 'lte'];
export const MAP_TARGETS = [
  'CLN.Allergy',
  'CLN.MedicationStatement',
  'CLN.MedicalHistory',
  'CLN.SpecialCondition',
  'CLN.VitalSign',
  'PAT.Occupation',
  'PAT.Contact.email',
  'PAT.Contact.phone',
];

function emptyDefinition(): FormDefinitionJson {
  return { schemaVersion: 1, sections: [{ key: 'main', title: { fa: '' }, fields: [] }] };
}

function parseValue(text: string): string | number | boolean {
  if (text === 'true' || text === 'false') {
    return text === 'true';
  }
  const number = Number(text);
  return text.trim() !== '' && !Number.isNaN(number) ? number : text;
}

function FieldEditor({
  field,
  previous,
  onChange,
  onRemove,
  onMove,
  index,
}: {
  field: FormFieldDef;
  previous: FormFieldDef[];
  onChange: (field: FormFieldDef) => void;
  onRemove: () => void;
  onMove: (delta: number) => void;
  index: number;
}) {
  const { t } = useTranslation('doc');
  const testId = `builder-field-${index}`;
  const set = (patch: Partial<FormFieldDef>) => onChange({ ...field, ...patch });
  const choice = field.type === 'singleChoice' || field.type === 'multiChoice';

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }} data-testid={testId}>
      <Grid container spacing={1.5}>
        <Grid size={{ xs: 12, md: 3 }}>
          <TextField select fullWidth size="small" label={t('doc:builder.field_type')} value={field.type} onChange={(event) => set({ type: event.target.value })} slotProps={{ htmlInput: { 'data-testid': `${testId}-type` } }}>
            {FIELD_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {t(`doc:field_type.${type}`)}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <TextField fullWidth size="small" label={t('doc:builder.field_key')} value={field.key} onChange={(event) => set({ key: event.target.value.trim() })} slotProps={{ htmlInput: { 'data-testid': `${testId}-key`, dir: 'ltr' } }} />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <TextField
            fullWidth
            size="small"
            label={t('doc:builder.label_fa')}
            value={(field.type === 'info' ? field.text?.fa : field.label?.fa) ?? ''}
            onChange={(event) => (field.type === 'info' ? set({ text: { ...field.text, fa: event.target.value } }) : set({ label: { ...field.label, fa: event.target.value } }))}
            slotProps={{ htmlInput: { 'data-testid': `${testId}-label` } }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <TextField fullWidth size="small" label={t('doc:builder.label_en')} value={field.label?.en ?? ''} onChange={(event) => set({ label: { ...field.label, en: event.target.value } })} slotProps={{ htmlInput: { dir: 'ltr' } }} />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <FormControlLabel control={<Checkbox checked={field.required ?? false} onChange={(event) => set({ required: event.target.checked })} slotProps={{ input: { 'data-testid': `${testId}-required` } as never }} />} label={t('doc:builder.required')} />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <TextField select fullWidth size="small" label={t('doc:builder.sensitivity')} value={field.sensitivity ?? 'General'} onChange={(event) => set({ sensitivity: event.target.value })}>
            {['General', 'Clinical', 'HighlySensitive'].map((value) => (
              <MenuItem key={value} value={value}>
                {t(`doc:sensitivity.${value}`)}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <TextField select fullWidth size="small" label={t('doc:builder.map_to')} value={field.mapTo ?? ''} onChange={(event) => set({ mapTo: event.target.value || undefined })} slotProps={{ htmlInput: { 'data-testid': `${testId}-map` } }}>
            <MenuItem value="">{t('doc:builder.no_mapping')}</MenuItem>
            {MAP_TARGETS.map((target) => (
              <MenuItem key={target} value={target}>
                <bdi dir="ltr">{target}</bdi>
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <TextField fullWidth size="small" label={t('doc:builder.help')} value={field.help?.fa ?? ''} onChange={(event) => set({ help: event.target.value ? { fa: event.target.value } : undefined })} />
        </Grid>
        {choice ? (
          <Grid size={12}>
            <TextField
              fullWidth
              size="small"
              label={t('doc:builder.options')}
              helperText={t('doc:builder.options_hint')}
              value={(field.options ?? []).map((option) => (option.label?.fa && option.label.fa !== option.value ? `${option.value}=${option.label.fa}` : option.value)).join(', ')}
              onChange={(event) =>
                set({
                  options: event.target.value
                    .split(/[،,]/)
                    .map((part) => part.trim())
                    .filter(Boolean)
                    .map((part) => {
                      const [value, label] = part.split('=');
                      // امتیاز گزینه هم‌نام حفظ می‌شود (US-DOC-022).
                      const score = field.options?.find((option) => option.value === value!.trim())?.score;
                      return { value: value!.trim(), label: { fa: (label ?? value)!.trim() }, ...(score === undefined ? {} : { score }) };
                    }),
                })
              }
              slotProps={{ htmlInput: { 'data-testid': `${testId}-options` } }}
            />
          </Grid>
        ) : null}
        {field.type === 'singleChoice' ? (
          <Grid size={12}>
            <TextField
              fullWidth
              size="small"
              label={t('doc:builder.option_scores')}
              helperText={t('doc:builder.option_scores_hint')}
              value={(field.options ?? []).map((option) => (option.score === undefined ? '' : String(option.score))).join(', ')}
              onChange={(event) => {
                const scores = event.target.value.split(/[،,]/).map((part) => part.trim());
                set({ options: (field.options ?? []).map((option, position) => { const raw = scores[position] ?? ''; const { score: _old, ...rest } = option; return raw === '' || Number.isNaN(Number(raw)) ? rest : { ...rest, score: Number(raw) }; }) });
              }}
              slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': `${testId}-scores` } }}
            />
          </Grid>
        ) : null}
        {field.type === 'scale' || field.type === 'number' ? (
          <>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField fullWidth size="small" type="number" label={t('doc:builder.min')} value={field.type === 'scale' ? (field.scale?.min ?? '') : (field.validation?.min ?? '')} onChange={(event) => (field.type === 'scale' ? set({ scale: { ...field.scale, min: Number(event.target.value) } }) : set({ validation: { ...field.validation, min: event.target.value === '' ? undefined : Number(event.target.value) } }))} />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField fullWidth size="small" type="number" label={t('doc:builder.max')} value={field.type === 'scale' ? (field.scale?.max ?? '') : (field.validation?.max ?? '')} onChange={(event) => (field.type === 'scale' ? set({ scale: { ...field.scale, max: Number(event.target.value) } }) : set({ validation: { ...field.validation, max: event.target.value === '' ? undefined : Number(event.target.value) } }))} />
            </Grid>
          </>
        ) : null}
        {field.type === 'shortText' || field.type === 'longText' ? (
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField fullWidth size="small" label={t('doc:builder.pattern')} value={field.validation?.pattern ?? ''} onChange={(event) => set({ validation: { ...field.validation, pattern: event.target.value || undefined } })} slotProps={{ htmlInput: { dir: 'ltr' } }} />
          </Grid>
        ) : null}
        {field.type === 'calculated' ? (
          <Grid size={12}>
            <Stack direction="row" spacing={1}>
              <TextField select size="small" label={t('doc:builder.formula')} value={field.formula?.op ?? 'sum'} onChange={(event) => set({ formula: { fields: field.formula?.fields ?? [], op: event.target.value } })} sx={{ minWidth: 120 }}>
                {['sum', 'avg', 'min', 'max'].map((op) => (
                  <MenuItem key={op} value={op}>
                    {t(`doc:builder.formula_op.${op}`)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField fullWidth size="small" label={t('doc:builder.formula_fields')} value={(field.formula?.fields ?? []).join(', ')} onChange={(event) => set({ formula: { op: field.formula?.op ?? 'sum', fields: event.target.value.split(/[،,]/).map((part) => part.trim()).filter(Boolean) } })} slotProps={{ htmlInput: { dir: 'ltr' } }} />
            </Stack>
          </Grid>
        ) : null}
        {field.type === 'table' ? (
          <Grid size={12}>
            <TextField fullWidth size="small" label={t('doc:builder.table_columns')} helperText={t('doc:builder.table_columns_hint')} value={(field.columns ?? []).map((column) => (column.type === 'number' ? `${column.key}:number` : column.key)).join(', ')} onChange={(event) => set({ columns: event.target.value.split(/[،,]/).map((part) => part.trim()).filter(Boolean).map((part) => { const [key, type] = part.split(':'); return { key: key!.trim(), label: { fa: key!.trim() }, type: type === 'number' ? 'number' : 'shortText' }; }) })} slotProps={{ htmlInput: { dir: 'ltr' } }} />
          </Grid>
        ) : null}
        <Grid size={12}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ alignItems: { md: 'center' } }}>
            <Typography variant="body2">{t('doc:builder.condition')}</Typography>
            <TextField
              select
              size="small"
              label={t('doc:builder.condition_field')}
              value={field.visibleIf?.field ?? ''}
              onChange={(event) => set({ visibleIf: event.target.value ? { field: event.target.value, op: field.visibleIf?.op ?? 'eq', value: field.visibleIf?.value ?? true } : undefined })}
              sx={{ minWidth: 200 }}
              slotProps={{ htmlInput: { 'data-testid': `${testId}-condition-field` } }}
            >
              <MenuItem value="">{t('doc:builder.always')}</MenuItem>
              <MenuItem value="patient.gender">{t('doc:builder.patient_gender')}</MenuItem>
              <MenuItem value="patient.age">{t('doc:builder.patient_age')}</MenuItem>
              {previous.filter((other) => other.key && other.type !== 'info').map((other) => (
                <MenuItem key={other.key} value={other.key}>
                  {other.label?.fa || other.key}
                </MenuItem>
              ))}
            </TextField>
            {field.visibleIf ? (
              <>
                <TextField select size="small" label={t('doc:builder.condition_op')} value={field.visibleIf.op} onChange={(event) => set({ visibleIf: { ...field.visibleIf!, op: event.target.value } })} sx={{ minWidth: 110 }} slotProps={{ htmlInput: { 'data-testid': `${testId}-condition-op` } }}>
                  {OPS.map((op) => (
                    <MenuItem key={op} value={op}>
                      {t(`doc:builder.ops.${op}`)}
                    </MenuItem>
                  ))}
                </TextField>
                {field.visibleIf.op !== 'truthy' ? (
                  <TextField size="small" label={t('doc:builder.condition_value')} value={field.visibleIf.value === undefined || field.visibleIf.value === null ? '' : String(field.visibleIf.value)} onChange={(event) => set({ visibleIf: { ...field.visibleIf!, value: parseValue(event.target.value) } })} slotProps={{ htmlInput: { 'data-testid': `${testId}-condition-value`, dir: 'ltr' } }} />
                ) : null}
              </>
            ) : null}
            <span style={{ flexGrow: 1 }} />
            <IconButton aria-label={t('doc:builder.move_up')} onClick={() => onMove(-1)}>
              <ArrowUpwardIcon />
            </IconButton>
            <IconButton aria-label={t('doc:builder.move_down')} onClick={() => onMove(1)}>
              <ArrowDownwardIcon />
            </IconButton>
            <IconButton aria-label={t('doc:builder.remove_field')} onClick={onRemove} data-testid={`${testId}-remove`}>
              <DeleteOutlineIcon />
            </IconButton>
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
}

function SectionEditor({ section, index, onChange, onRemove, before }: { section: FormSectionDef; index: number; onChange: (section: FormSectionDef) => void; onRemove: () => void; before: FormFieldDef[] }) {
  const { t } = useTranslation('doc');
  const update = (fieldIndex: number, field: FormFieldDef) => onChange({ ...section, fields: section.fields.map((item, position) => (position === fieldIndex ? field : item)) });
  const move = (fieldIndex: number, delta: number) => {
    const target = fieldIndex + delta;
    if (target < 0 || target >= section.fields.length) {
      return;
    }
    const fields = [...section.fields];
    [fields[fieldIndex], fields[target]] = [fields[target]!, fields[fieldIndex]!];
    onChange({ ...section, fields });
  };
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid={`builder-section-${index}`}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1}>
          <TextField size="small" label={t('doc:builder.section_key')} value={section.key} onChange={(event) => onChange({ ...section, key: event.target.value.trim() })} slotProps={{ htmlInput: { dir: 'ltr' } }} />
          <TextField size="small" fullWidth label={t('doc:builder.section_title')} value={section.title?.fa ?? ''} onChange={(event) => onChange({ ...section, title: { ...section.title, fa: event.target.value } })} slotProps={{ htmlInput: { 'data-testid': `builder-section-${index}-title` } }} />
          <IconButton aria-label={t('doc:builder.remove_section')} onClick={onRemove}>
            <DeleteOutlineIcon />
          </IconButton>
        </Stack>
        {section.fields.map((field, fieldIndex) => (
          <FieldEditor
            key={fieldIndex}
            index={before.length + fieldIndex}
            field={field}
            previous={[...before, ...section.fields.slice(0, fieldIndex)]}
            onChange={(next) => update(fieldIndex, next)}
            onRemove={() => onChange({ ...section, fields: section.fields.filter((_, position) => position !== fieldIndex) })}
            onMove={(delta) => move(fieldIndex, delta)}
          />
        ))}
        <Button startIcon={<AddIcon />} sx={{ alignSelf: 'flex-start' }} onClick={() => onChange({ ...section, fields: [...section.fields, { key: `field_${before.length + section.fields.length + 1}`, type: 'shortText', label: { fa: '' } }] })} data-testid={`builder-add-field-${index}`}>
          {t('doc:builder.add_field')}
        </Button>
      </Stack>
    </Paper>
  );
}

const LICENSE_STATUSES = ['free', 'licensed', 'restricted', 'unverified'] as const;
const SEVERITIES = ['none', 'low', 'moderate', 'high', 'critical'] as const;

/** «code:min-max:severity=برچسب» ← بازه تفسیر. */
function parseBands(text: string): ScoringBandDef[] {
  return text
    .split(/[،,]/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const [head, label] = part.split('=');
      const [code, range, severity] = head!.split(':').map((item) => item.trim());
      const [min, max] = (range ?? '').split('-').map((item) => Number(item.trim()));
      return { code: code ?? '', min: min ?? 0, max: max ?? 0, severity: severity && (SEVERITIES as readonly string[]).includes(severity) ? severity : 'none', label: { fa: (label ?? code ?? '').trim() } };
    });
}

function formatBands(bands: ScoringBandDef[] | undefined): string {
  return (bands ?? []).map((band) => `${band.code}:${band.min}-${band.max}:${band.severity ?? 'none'}${band.label?.fa && band.label.fa !== band.code ? `=${band.label.fa}` : ''}`).join(', ');
}

/** امتیازدهی پرسشنامه (US-DOC-022): ابزار، نسخه امتیازدهی (کلید سازگاری نمودار BR-CLN-114)، مجوز، مقیاس‌ها با فرمول جمع یا میانگین و بازه‌های تفسیر. */
function ScoringEditor({ definition, onChange }: { definition: FormDefinitionJson; onChange: (definition: FormDefinitionJson) => void }) {
  const { t } = useTranslation('doc');
  const scoring = definition.scoring;
  const set = (patch: Partial<ScoringDef>) => onChange({ ...definition, scoring: { ...(scoring as ScoringDef), ...patch } });
  const setScale = (index: number, patch: Partial<ScoringScaleDef>) => set({ scales: (scoring?.scales ?? []).map((scale, position) => (position === index ? { ...scale, ...patch } : scale)) });
  const scored = definition.sections.flatMap((section) => section.fields).filter((field) => field.type === 'singleChoice' || field.type === 'scale' || field.type === 'number').map((field) => field.key);
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="builder-scoring">
      <FormControlLabel
        control={
          <Checkbox
            checked={Boolean(scoring)}
            onChange={(event) => {
              if (event.target.checked) {
                onChange({ ...definition, scoring: { instrument: '', version: '1', license: { status: 'unverified' }, scales: [{ key: 'total', op: 'sum', items: scored, primary: true, bands: [] }] } });
              } else {
                const { scoring: _removed, ...rest } = definition;
                onChange(rest);
              }
            }}
            slotProps={{ input: { 'aria-label': t('doc:builder.scoring') } }}
            data-testid="builder-scoring-toggle"
          />
        }
        label={t('doc:builder.scoring')}
      />
      {scoring ? (
        <Stack spacing={1.5}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
            <TextField size="small" required label={t('doc:builder.instrument')} value={scoring.instrument} onChange={(event) => set({ instrument: event.target.value })} slotProps={{ htmlInput: { dir: 'ltr', maxLength: 40, 'data-testid': 'builder-scoring-instrument' } }} />
            <TextField size="small" required label={t('doc:builder.scoring_version')} helperText={t('doc:builder.scoring_version_hint')} value={scoring.version} onChange={(event) => set({ version: event.target.value })} slotProps={{ htmlInput: { dir: 'ltr', maxLength: 20 } }} />
            <TextField select size="small" label={t('doc:builder.license')} value={scoring.license.status} onChange={(event) => set({ license: { ...scoring.license, status: event.target.value } })} sx={{ minWidth: 160 }} slotProps={{ htmlInput: { 'data-testid': 'builder-scoring-license' } }}>
              {LICENSE_STATUSES.map((status) => (
                <MenuItem key={status} value={status}>
                  {t(`doc:license.${status}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField size="small" fullWidth label={t('doc:builder.license_holder')} value={scoring.license.holder ?? ''} onChange={(event) => set({ license: { ...scoring.license, holder: event.target.value || undefined } })} />
          </Stack>
          {scoring.scales.map((scale, index) => (
            <Stack key={index} spacing={1} data-testid={`builder-scale-${index}`}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                <TextField size="small" label={t('doc:builder.scale_key')} value={scale.key} onChange={(event) => setScale(index, { key: event.target.value.trim() })} slotProps={{ htmlInput: { dir: 'ltr' } }} />
                <TextField size="small" label={t('doc:builder.scale_label')} value={scale.label?.fa ?? ''} onChange={(event) => setScale(index, { label: { fa: event.target.value } })} />
                <TextField select size="small" label={t('doc:builder.formula')} value={scale.op ?? 'sum'} onChange={(event) => setScale(index, { op: event.target.value })} sx={{ minWidth: 120 }}>
                  {['sum', 'avg'].map((op) => (
                    <MenuItem key={op} value={op}>
                      {t(`doc:builder.formula_op.${op}`)}
                    </MenuItem>
                  ))}
                </TextField>
                <FormControlLabel control={<Checkbox checked={Boolean(scale.primary)} onChange={(event) => set({ scales: scoring.scales.map((item, position) => ({ ...item, primary: position === index ? event.target.checked : false })) })} />} label={t('doc:builder.primary')} />
                <IconButton aria-label={t('doc:builder.remove_scale')} onClick={() => set({ scales: scoring.scales.filter((_item, position) => position !== index) })} disabled={scoring.scales.length === 1}>
                  <DeleteOutlineIcon />
                </IconButton>
              </Stack>
              <TextField size="small" fullWidth label={t('doc:builder.scale_items')} value={scale.items.join(', ')} onChange={(event) => setScale(index, { items: event.target.value.split(/[،,]/).map((part) => part.trim()).filter(Boolean) })} slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': `builder-scale-${index}-items` } }} />
              <BandsField value={scale.bands} onChange={(bands) => setScale(index, { bands })} testId={`builder-scale-${index}-bands`} />
            </Stack>
          ))}
          <Button startIcon={<AddIcon />} sx={{ alignSelf: 'flex-start' }} onClick={() => set({ scales: [...scoring.scales, { key: `scale_${scoring.scales.length + 1}`, op: 'sum', items: [], bands: [] }] })}>
            {t('doc:builder.add_scale')}
          </Button>
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t('doc:builder.scoring_hint')}
        </Typography>
      )}
    </Paper>
  );
}

/** ویرایش متنی بازه‌ها؛ متن خام تا خروج از فیلد نگه داشته می‌شود تا تایپ نیمه‌کاره بازنویسی نشود. */
function BandsField({ value, onChange, testId }: { value: ScoringBandDef[] | undefined; onChange: (bands: ScoringBandDef[]) => void; testId: string }) {
  const { t } = useTranslation('doc');
  const [text, setText] = useState(formatBands(value));
  return (
    <TextField
      size="small"
      fullWidth
      label={t('doc:builder.bands')}
      helperText={t('doc:builder.bands_hint')}
      value={text}
      onChange={(event) => setText(event.target.value)}
      onBlur={() => onChange(parseBands(text))}
      slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': testId } }}
    />
  );
}

export function FormBuilder({ definition, onChange }: { definition: FormDefinitionJson; onChange: (definition: FormDefinitionJson) => void }) {
  const { t } = useTranslation('doc');
  const [tab, setTab] = useState<'edit' | 'preview'>('edit');
  const [answers, setAnswers] = useState<Answers>({});
  const [gender, setGender] = useState('Female');
  const [age, setAge] = useState('30');
  let before: FormFieldDef[] = [];
  return (
    <Stack spacing={2}>
      <Tabs value={tab} onChange={(_event, next: 'edit' | 'preview') => setTab(next)} aria-label={t('doc:builder.title')}>
        <Tab value="edit" label={t('doc:builder.edit')} data-testid="builder-tab-edit" />
        <Tab value="preview" label={t('doc:builder.preview')} data-testid="builder-tab-preview" />
      </Tabs>
      {tab === 'edit' ? (
        <Stack spacing={2}>
          {definition.sections.map((section, index) => {
            const element = (
              <SectionEditor
                key={index}
                index={index}
                section={section}
                before={before}
                onChange={(next) => onChange({ ...definition, sections: definition.sections.map((item, position) => (position === index ? next : item)) })}
                onRemove={() => onChange({ ...definition, sections: definition.sections.filter((_, position) => position !== index) })}
              />
            );
            before = [...before, ...section.fields];
            return element;
          })}
          <Button startIcon={<AddIcon />} sx={{ alignSelf: 'flex-start' }} onClick={() => onChange({ ...definition, sections: [...definition.sections, { key: `section_${definition.sections.length + 1}`, title: { fa: '' }, fields: [] }] })} data-testid="builder-add-section">
            {t('doc:builder.add_section')}
          </Button>
          <ScoringEditor definition={definition} onChange={onChange} />
        </Stack>
      ) : (
        <Stack spacing={2} data-testid="builder-preview">
          <Stack direction="row" spacing={1}>
            <TextField select size="small" label={t('doc:builder.preview_gender')} value={gender} onChange={(event) => setGender(event.target.value)} sx={{ minWidth: 140 }} slotProps={{ htmlInput: { 'data-testid': 'builder-preview-gender' } }}>
              {['Female', 'Male'].map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`doc:gender.${value}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField size="small" label={t('doc:builder.preview_age')} value={age} onChange={(event) => setAge(event.target.value)} sx={{ width: 120 }} />
          </Stack>
          <FormRenderer definition={definition} answers={answers} onChange={setAnswers} patient={{ gender, age: Number(age) }} testPrefix="preview-field" />
        </Stack>
      )}
    </Stack>
  );
}

function EditorDialog({ template, onClose }: { template: DocFormTemplateView | null; onClose: () => void }) {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [code, setCode] = useState(template?.code ?? '');
  const [name, setName] = useState(template?.name ?? '');
  const [description, setDescription] = useState(template?.description ?? '');
  const [type, setType] = useState<DocFormType>(template?.type ?? 'Questionnaire');
  const [definition, setDefinition] = useState<FormDefinitionJson>(template ? asDefinition(template.definition) : emptyDefinition());
  const [dirty, setDirty] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      template
        ? unwrap(tenantApi.PUT('/api/v1/documents/form-templates/{id}', { params: { path: { id: template.id } }, body: { code: null, name, description: description || null, type, definition: definition as never, rowVersion: num(template.rowVersion) } }))
        : unwrap(tenantApi.POST('/api/v1/documents/form-templates', { body: { code, name, description: description || null, type, definition: definition as never, rowVersion: null } })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: docKeys.formTemplates(session?.tenantId) });
      onClose();
    },
  });
  const change = (next: FormDefinitionJson) => {
    setDirty(true);
    setDefinition(next);
  };
  return (
    <FormDialog open titleKey={template ? 'doc:builder.edit_title' : 'doc:builder.create_title'} screenId={SCR_STAFF_601} onSubmit={() => save.mutate()} onClose={onClose} isDirty={dirty} isSubmitting={save.isPending} submitError={save.error} maxWidth="lg">
      {template?.isSample ? (
        <SampleContentBanner>
          <SampleBadge /> {t('doc:builder.sample_hint')}
        </SampleContentBanner>
      ) : null}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
        <TextField label={t('doc:builder.code')} value={code} onChange={(event) => setCode(event.target.value)} disabled={!!template} required slotProps={{ htmlInput: { 'data-testid': 'builder-code', dir: 'ltr' } }} />
        <TextField label={t('doc:builder.name')} value={name} onChange={(event) => { setDirty(true); setName(event.target.value); }} required fullWidth slotProps={{ htmlInput: { 'data-testid': 'builder-name' } }} />
        <TextField select label={t('doc:builder.form_type')} value={type} onChange={(event) => { setDirty(true); setType(event.target.value as DocFormType); }} sx={{ minWidth: 180 }} slotProps={{ htmlInput: { 'data-testid': 'builder-type' } }}>
          {FORM_TYPES.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`doc:form_type.${value}`)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <TextField label={t('doc:builder.description')} value={description} onChange={(event) => { setDirty(true); setDescription(event.target.value); }} />
      <FormBuilder definition={definition} onChange={change} />
    </FormDialog>
  );
}

function LibraryDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const library = useQuery({ queryKey: docKeys.formLibrary(session?.tenantId), queryFn: () => unwrap(tenantApi.GET('/api/v1/documents/form-templates/library')) });
  const importSample = useMutation({
    mutationFn: (sampleRef: string) => unwrap(tenantApi.POST('/api/v1/documents/form-templates:import-sample', { body: { sampleRef } })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: docKeys.all(session?.tenantId) });
      onClose();
    },
  });
  return (
    <FormDialog open titleKey="doc:builder.library_title" screenId={SCR_STAFF_601} onSubmit={onClose} onClose={onClose} isDirty={false} isSubmitting={importSample.isPending} submitError={importSample.error} submitKey="doc:actions.close">
      {library.error ? <ErrorState error={library.error} /> : null}
      {(library.data?.items ?? []).map((item) => (
        <Stack key={item.sampleRef} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <SampleBadge />
          <Typography sx={{ flexGrow: 1 }}>{item.name}</Typography>
          {item.instrument ? <Chip size="small" variant="outlined" label={item.instrument} /> : null}
          {item.licenseStatus ? <Chip size="small" color={item.licenseStatus === 'free' ? 'success' : 'warning'} variant="outlined" label={t(`doc:license.${item.licenseStatus}`)} data-testid={`library-license-${item.sampleRef}`} /> : null}
          <Button size="small" disabled={item.imported || importSample.isPending} onClick={() => importSample.mutate(item.sampleRef)} data-testid={`library-import-${item.sampleRef}`}>
            {item.imported ? t('doc:builder.imported') : t('doc:builder.import')}
          </Button>
        </Stack>
      ))}
    </FormDialog>
  );
}

export function FormTemplatesPage() {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('doc.form_template.manage');
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<DocFormTemplateView | null | 'new'>(null);
  const [library, setLibrary] = useState(false);
  const templates = useQuery({ queryKey: docKeys.formTemplates(session?.tenantId), queryFn: () => unwrap(tenantApi.GET('/api/v1/documents/form-templates', { params: { query: {} } })) });
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: docKeys.all(session?.tenantId) });
  const publish = useMutation({
    mutationFn: (row: DocFormTemplateView) => unwrap(tenantApi.POST('/api/v1/documents/form-templates/{id}:publish', { params: { path: { id: row.id } }, body: { rowVersion: num(row.rowVersion) } })),
    onSuccess: invalidate,
  });
  const archive = useMutation({
    mutationFn: (row: DocFormTemplateView) =>
      row.status === 'Archived'
        ? unwrap(tenantApi.POST('/api/v1/documents/form-templates/{id}:restore', { params: { path: { id: row.id } } }))
        : unwrap(tenantApi.POST('/api/v1/documents/form-templates/{id}:archive', { params: { path: { id: row.id } } })),
    onSuccess: invalidate,
  });
  const openEditor = async (row: DocFormTemplateView) => {
    const full = await unwrap(tenantApi.GET('/api/v1/documents/form-templates/{id}', { params: { path: { id: row.id } } }));
    setEditing(full);
  };

  const columns: DataTableColumn<DocFormTemplateView>[] = [
    {
      id: 'name',
      headerKey: 'doc:builder.columns.name',
      cell: (row) => (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <span>{row.name}</span>
          {row.isSample ? <SampleBadge /> : null}
        </Stack>
      ),
    },
    { id: 'type', headerKey: 'doc:builder.columns.type', cell: (row) => t(`doc:form_type.${row.type}`) },
    { id: 'status', headerKey: 'doc:builder.columns.status', cell: (row) => <span data-testid={`form-template-status-${row.code}`}>{t(`doc:template_status.${row.status}`)}</span> },
    { id: 'version', headerKey: 'doc:builder.columns.version', cell: (row) => (num(row.currentVersion) > 0 ? faDigits(num(row.currentVersion)) : '—') + (row.hasDraftChanges ? ` · ${t('doc:builder.draft_changes')}` : '') },
    {
      id: 'actions',
      headerKey: 'doc:builder.columns.actions',
      cell: (row) =>
        write.canWrite ? (
          <Stack direction="row" spacing={0.5}>
            <Button size="small" onClick={() => void openEditor(row)} data-testid={`form-template-edit-${row.code}`}>
              {t('doc:actions.edit')}
            </Button>
            {row.hasDraftChanges && row.status !== 'Archived' ? (
              <Button size="small" onClick={() => publish.mutate(row)} data-testid={`form-template-publish-${row.code}`}>
                {t('doc:builder.publish')}
              </Button>
            ) : null}
            <Button size="small" color="warning" onClick={() => archive.mutate(row)}>
              {row.status === 'Archived' ? t('doc:builder.restore') : t('doc:builder.archive')}
            </Button>
          </Stack>
        ) : null,
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_601}
      titleKey="doc:builder.title"
      subtitleKey="doc:builder.subtitle"
      actions={
        write.canWrite ? (
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => setLibrary(true)} data-testid="form-library">
              {t('doc:builder.library')}
            </Button>
            <Button variant="contained" onClick={() => setEditing('new')} data-testid="form-template-create">
              {t('doc:builder.create')}
            </Button>
          </Stack>
        ) : undefined
      }
      filters={<WriteNotice write={write} />}
    >
      {publish.error ? <ErrorState error={publish.error} compact /> : null}
      {archive.error ? <ErrorState error={archive.error} compact /> : null}
      <DataTable captionKey="doc:builder.title" columns={columns} rows={templates.data?.items} getRowId={(row) => row.id} isLoading={templates.isPending} error={templates.error} onRetry={() => void templates.refetch()} emptyTitleKey="doc:builder.empty" />
      {editing ? <EditorDialog template={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
      {library ? <LibraryDialog onClose={() => setLibrary(false)} /> : null}
    </ListPage>
  );
}
